import type { BlogRecord } from '@/lib/blocks/collection/records';
import { readNumber, readRecord, readString } from '@/lib/prop-utils';
import type { Topic } from '@/types/shared';
import { TEMPLATE_PREVIEW_TOPICS } from './preview-data';

function normalizeBlogImage(blog: BlogRecord): BlogRecord['image'] {
  const imageUrl = readString(blog.image?.url) ?? readString(blog.imageUrl);

  if (!imageUrl) return blog.image;

  return {
    ...(blog.image ?? {}),
    url: imageUrl,
  };
}

function normalizeTopic(topic: unknown): Topic | null {
  const stringTopic = readString(topic);
  if (stringTopic) {
    const name = stringTopic.trim();
    return name ? { id: name, name } : null;
  }

  const record = readRecord(topic);
  if (!record) return null;

  const name = readString(record.name)?.trim();
  if (!name) return null;

  return {
    id: readString(record.id) ?? name,
    name,
    usageCount: readNumber(record.usageCount),
  };
}

function normalizeBlogTopics(blog: BlogRecord): Topic[] | undefined {
  if (!Array.isArray(blog.topics)) return blog.topics;

  return blog.topics.map(normalizeTopic).filter((topic): topic is Topic => Boolean(topic));
}

function deriveTopicsFromBlogs(blogs: BlogRecord[]): Topic[] {
  const topicsByName = new Map<string, Topic>();

  for (const blog of blogs) {
    for (const topic of blog.topics ?? []) {
      const name = readString(topic.name)?.trim();
      if (!name || topicsByName.has(name.toLowerCase())) continue;
      topicsByName.set(name.toLowerCase(), topic);
    }
  }

  return Array.from(topicsByName.values());
}

export function resolveBlogFilterTopics({
  sectionTopics,
  blogs,
  isTemplatePreview,
}: {
  sectionTopics?: Topic[];
  blogs: BlogRecord[];
  isTemplatePreview: boolean;
}): Topic[] {
  if (sectionTopics?.length) return sectionTopics;

  const derivedTopics = deriveTopicsFromBlogs(blogs);
  if (derivedTopics.length > 0) return derivedTopics;

  return isTemplatePreview ? TEMPLATE_PREVIEW_TOPICS : [];
}

export function normalizeBlog(blog: BlogRecord): BlogRecord {
  return {
    ...blog,
    image: normalizeBlogImage(blog),
    topics: normalizeBlogTopics(blog),
  };
}
